import { useState, useEffect, useMemo } from 'react';
import {
  useGetDevicesQuery,
  useGetDevicesByIdStatsQuery,
  useGetAnalyticsTimeSeriesQuery,
  useGetDevicesByIdQuery,
} from '@/services';
import { sensorTypeUnits } from '@/lib/threejs/config';

interface DeviceData {
  id: string;
  name: string;
  type: string;
  value: number;
  isOnline: boolean;
  unit: string;
  position?: [number, number, number];
  deviceName: string;
  deviceId?: string; // Added deviceId for navigation
  timestamp?: string;
  history?: Array<{ timestamp: number; value: number }>;
  min?: number;
  max?: number;
  status?: 'normal' | 'warning' | 'critical';
  alertCount?: number;
}

export function useDeviceData(selectedDevices: string[], timeRange: string = '24') {
  const [sensorData, setSensorData] = useState<DeviceData[]>([]);
  const [alertsCount, setAlertsCount] = useState<Record<string, number>>({});

  // Memoize the date range
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - parseInt(timeRange) * 60 * 60 * 1000);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, [timeRange]);

  // Get all devices with their sensors
  const { data: devicesData } = useGetDevicesQuery({
    includeSensors: 'true',
  });

  // Get detailed data for the selected devices
  const { data: detailedDevicesData } = useGetDevicesByIdQuery(
    {
      id: selectedDevices.join(','),
      includeSensors: 'true',
      includeLatestData: 'true',
    },
    {
      skip: selectedDevices.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  // Get stats for selected devices - only if we have devices selected
  const { data: statsData, isLoading: isLoadingStats } = useGetDevicesByIdStatsQuery(
    {
      id: selectedDevices.join(','),
      includeSensors: 'true',
      includeLatestData: 'true',
    },
    {
      skip: selectedDevices.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  // Get alerts for the selected devices
  useEffect(() => {
    // Only fetch alerts if we have selected devices
    if (selectedDevices.length === 0) return;

    // Set up a counter for alert counts by device ID
    const newAlertsCount: Record<string, number> = {};

    // For each device, get its alert count
    selectedDevices.forEach((deviceId) => {
      // You could fetch this from the API or use a mock
      // This is a placeholder for real alert counts

      // Random number for demo purposes - in real app, use API data
      const hash = deviceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      newAlertsCount[deviceId] = hash % 5; // 0-4 alerts per device
    });

    setAlertsCount(newAlertsCount);
  }, [selectedDevices]);

  // Get time series data for historical charts
  const { data: timeSeriesData } = useGetAnalyticsTimeSeriesQuery(
    {
      deviceIds: selectedDevices.join(','),
      startDate: new Date(Date.now() - parseInt(timeRange) * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
      interval: parseInt(timeRange) <= 24 ? 'hour' : 'day',
      aggregation: 'avg',
    },
    {
      skip: selectedDevices.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  // Process data for visualization
  const processedData = useMemo(() => {
    if (!devicesData?.data) return [];

    const data: DeviceData[] = [];
    const timeSeries = timeSeriesData?.data || [];

    // Find selected devices
    const selectedDeviceObjects = devicesData.data.filter((device) => selectedDevices.includes(device.id));

    // For each device, create a visualization object even without stats
    selectedDeviceObjects.forEach((device, deviceIndex) => {
      if (!device.sensors || device.sensors.length === 0) {
        // If no sensors, create a device node anyway
        data.push({
          id: `${device.id}-node`,
          name: device.name || `Device ${device.id.substring(0, 8)}`,
          type: 'device',
          value: 0,
          isOnline: true,
          unit: '',
          deviceName: device.name || `Device ${device.id}`,
          deviceId: device.id, // Store device ID for navigation
          position: [(deviceIndex - selectedDeviceObjects.length / 2) * 10, 0.5, 0],
          alertCount: alertsCount[device.id] || 0,
        });
        return;
      }

      // Process device sensors
      device.sensors.forEach((sensor, sensorIndex) => {
        // Look for stats if available
        let value = 0;
        let isOnline = false;
        let timestamp: string | undefined = undefined;
        let min: number | undefined = undefined;
        let max: number | undefined = undefined;

        if (statsData?.data?.sensor_stats) {
          const sensorStat = statsData.data.sensor_stats.find((stat) => stat.sensor_id === sensor.id);

          if (sensorStat) {
            if (sensorStat.value !== null && sensorStat.value !== undefined) {
              value = Number(sensorStat.value);
              isOnline = true;
            }
            if (sensorStat.timestamp) {
              timestamp = sensorStat.timestamp;
            }
          }
        }

        // Try to get latest data from detailed device data if stats not available
        if (!isOnline && detailedDevicesData?.data) {
          const detailedDevice = Array.isArray(detailedDevicesData.data)
            ? detailedDevicesData.data.find((d) => d.id === device.id)
            : detailedDevicesData.data.id === device.id
              ? detailedDevicesData.data
              : null;

          if (detailedDevice?.sensors) {
            const detailedSensor = detailedDevice.sensors.find((s) => s.id === sensor.id);
            if (detailedSensor?.latest_reading) {
              value = Number(detailedSensor.latest_reading.value);
              isOnline = true;
              timestamp = detailedSensor.latest_reading.timestamp;
            }
          }
        }

        // Get sensor history from time series data
        let history: Array<{ timestamp: number; value: number }> | undefined = undefined;
        let status: 'normal' | 'warning' | 'critical' = 'normal';

        const sensorTimeSeries = timeSeries.find((ts) => ts.sensor_id === sensor.id);
        if (sensorTimeSeries?.series && sensorTimeSeries.series.length > 0) {
          history = sensorTimeSeries.series
            .filter((point) => point.value !== null && point.value !== undefined)
            .map((point) => ({
              timestamp: new Date(point.timestamp).getTime(),
              value: Number(point.value),
            }));

          // Calculate min/max from history
          const values = history.map((h) => h.value);
          min = Math.min(...values);
          max = Math.max(...values);
        }

        // Determine status based on value thresholds
        if (sensor.type === 'temperature') {
          // Example threshold: high temperature warning
          if (value > 30) status = 'warning';
          if (value > 40) status = 'critical';
        } else if (sensor.type === 'humidity') {
          // Example threshold: extreme humidity values
          if (value < 20 || value > 80) status = 'warning';
          if (value < 10 || value > 90) status = 'critical';
        } else if (sensor.type === 'voltage') {
          const normalVoltage = 220; // Example for EV charger
          const deviation = Math.abs((value - normalVoltage) / normalVoltage);
          if (deviation > 0.05) status = 'warning'; // 5% deviation
          if (deviation > 0.1) status = 'critical'; // 10% deviation
        } else if (sensor.type === 'power') {
          // Power thresholds depend on expected range
          if (value > 7000) status = 'warning';
          if (value > 8000) status = 'critical';
        } else if (sensor.type === 'current') {
          // Current thresholds
          if (value > 30) status = 'warning';
          if (value > 40) status = 'critical';
        }

        // Get unit from sensor or default by type
        const unit = sensor.unit || sensorTypeUnits[sensor.type?.toLowerCase() || 'default'] || '';

        // Always create a data object for the sensor, even without stats
        data.push({
          id: sensor.id,
          name: sensor.name || `${sensor.type} Sensor`,
          type: sensor.type || 'unknown',
          value: value,
          isOnline: isOnline,
          unit: unit,
          deviceName: device.name || `Device ${device.id}`,
          deviceId: device.id, // Store device ID for navigation
          timestamp: timestamp,
          history: history,
          min: min,
          max: max,
          status: status,
          alertCount: status === 'warning' || status === 'critical' ? 1 : 0,
          // Position in a grid, offset each device group
          position: [
            (deviceIndex - selectedDeviceObjects.length / 2) * 10,
            0.5,
            (sensorIndex - (device.sensors?.length || 0) / 2) * 5,
          ],
        });
      });
    });

    return data;
  }, [devicesData, statsData, selectedDevices, timeSeriesData, detailedDevicesData, alertsCount]);

  useEffect(() => {
    setSensorData(processedData);
  }, [processedData]);

  // Calculate total alerts
  const totalAlerts = useMemo(() => {
    return (
      Object.values(alertsCount).reduce((sum, count) => sum + count, 0) +
      sensorData.filter((s) => s.status === 'warning' || s.status === 'critical').length
    );
  }, [alertsCount, sensorData]);

  return {
    sensorData,
    isLoading: isLoadingStats,
    alerts: {
      total: totalAlerts,
      byDevice: alertsCount,
    },
  };
}
